import React, { useEffect, useState } from "react";
import { Play, Radio, Sparkles, Zap } from "lucide-react";

const FALLBACK_ART = [
  "linear-gradient(135deg, rgba(0,255,190,.22), rgba(0,0,0,.92))",
  "linear-gradient(135deg, rgba(255,0,170,.20), rgba(0,0,0,.94))",
  "linear-gradient(135deg, rgba(70,130,255,.22), rgba(0,0,0,.94))",
];

function artUrl(v) {
  const raw =
    v?.poster_url ||
    v?.tmdb_poster_url ||
    v?.show_poster_url ||
    v?.series_poster_url ||
    v?.backdrop_url ||
    v?.still_url ||
    v?.image_url ||
    v?.poster ||
    v?.thumbnail_url ||
    v?.artwork_url ||
    v?.poster_path ||
    v?.backdrop_path ||
    v?.still_path ||
    null;

  if (!raw) return null;
  if (typeof raw !== "string") return null;
  if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("/api/")) return raw;

  // TMDb-style image paths are safe to render directly from the public image CDN.
  if (raw.startsWith("/")) return `https://image.tmdb.org/t/p/w780${raw}`;
  return raw;
}

function initials(title = "") {
  return String(title)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "TV";
}

function FeatureCard({ item, index }) {
  return (
    <div className="relative overflow-hidden border-l-2 border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] bg-[color-mix(in_srgb,var(--phosphor)_4%,black)] px-4 py-3 min-h-[104px]" data-testid={`updates-feature-${item.id}`}>
      <div className="absolute inset-y-0 left-0 w-1 bg-[var(--phosphor)]" style={{ opacity: 0.55 + ((index % 3) * 0.12) }} />
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-[9px] tracking-[0.26em] opacity-65">{item.kicker}</span>
        <span className="text-[8px] tracking-[0.18em] opacity-45">{item.tag || "NEW"}</span>
      </div>
      <div className="text-sm md:text-base font-bold tracking-[0.08em] mb-1.5 phosphor-glow">{item.title}</div>
      <div className="text-[11px] md:text-xs leading-relaxed opacity-75">{item.body}</div>
    </div>
  );
}

function RecentCard({ video, index, onTuneToVideo }) {
  const art = artUrl(video);
  return (
    <button
      type="button"
      onClick={() => onTuneToVideo && onTuneToVideo(video)}
      className="group relative min-w-0 text-left border border-[color-mix(in_srgb,var(--phosphor)_24%,transparent)] bg-black/75 hover:border-[var(--phosphor)] transition-all overflow-hidden"
      data-testid={`updates-recent-row-${video.id}`}
      title={`Tune to ${video.title}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden border-b border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)]" style={{ background: art ? "#050505" : FALLBACK_ART[index % FALLBACK_ART.length] }}>
        {art ? (
          <img src={art} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300" loading="lazy" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
            <Radio size={22} strokeWidth={1.4} className="opacity-35 mb-3" />
            <div className="text-3xl md:text-4xl tracking-[0.18em] font-bold opacity-35 phosphor-glow">
              {initials(video.title)}
            </div>
            <div className="mt-2 text-[8px] tracking-[0.20em] opacity-35 line-clamp-2">
              {video.title}
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
        <span className="absolute top-2 left-2 text-[8px] tracking-[0.16em] px-1.5 py-0.5 bg-[var(--phosphor)] text-black font-bold">JUST ADDED</span>
        <span className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/75 border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] flex items-center justify-center group-hover:bg-[var(--phosphor)] group-hover:text-black transition-colors">
          <Play size={12} fill="currentColor" />
        </span>
      </div>
      <div className="p-3">
        <div className="text-[11px] md:text-xs font-bold truncate mb-1">{video.title}</div>
        <div className="text-[9px] opacity-55 truncate">
          CH {String(video.channel_number || 0).padStart(2, "0")}{video.channel_name ? ` · ${video.channel_name}` : ""}
        </div>
        {(video.year || video.genre) && <div className="text-[9px] opacity-45 truncate mt-0.5">{[video.year, video.genre].filter(Boolean).join(" · ")}</div>}
      </div>
    </button>
  );
}

export default function UpdatesOverlay({ notes, features: releaseFeatures, recentVideos, onClose, onTuneToVideo }) {
  const [phase, setPhase] = useState("dialup");
  const [scanLine, setScanLine] = useState(0);

  useEffect(() => {
    const stages = [
      setTimeout(() => setPhase("handshake"), 700),
      setTimeout(() => setPhase("download"), 1450),
      setTimeout(() => setPhase("ready"), 2500),
    ];
    return () => stages.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== "download") return undefined;
    const iv = setInterval(() => setScanLine((v) => (v + 7) % 101), 90);
    return () => clearInterval(iv);
  }, [phase]);

  const features = Array.isArray(releaseFeatures) ? releaseFeatures.slice(0, 6) : [];
  const recents = Array.isArray(recentVideos) ? recentVideos.slice(0, 12) : [];
  const statusCopy = {
    dialup: ["CONNECTING TO SCARTLEAD NETWORK…", "DIAL TONE DETECTED"],
    handshake: ["HANDSHAKE ACCEPTED", "56K LINK · SIGNAL GOOD"],
    download: ["DOWNLOADING WHAT'S NEW…", `${String(scanLine).padStart(3, "0")}% COMPLETE`],
  };

  if (phase !== "ready") {
    const [headline, sub] = statusCopy[phase] || statusCopy.dialup;
    return (
      <div className="absolute inset-0 z-30 bg-black text-[var(--phosphor)] flex items-center justify-center overflow-hidden" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="updates-overlay">
        <div className="absolute top-0 left-0 right-0 h-3 flex">
          {["#C7C700", "#00C7C7", "#00C700", "#C700C7", "#C70000", "#0000C7", "#FFFFFF"].map((c) => <div key={c} style={{ background: c, flex: 1 }} />)}
        </div>
        <div className="absolute inset-0 pointer-events-none opacity-35" style={{ background: "repeating-linear-gradient(0deg, color-mix(in srgb, var(--phosphor) 6%, transparent) 0 1px, transparent 1px 3px)" }} />
        <div className="relative w-[min(86%,560px)] border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black/95 p-7 shadow-[0_0_34px_color-mix(in_srgb,var(--phosphor)_15%,transparent)]">
          <div className="flex items-center justify-between text-[9px] tracking-[0.25em] opacity-55 mb-8"><span>SCARTLEAD ONLINE</span><span>MODEM 56K</span></div>
          <div className="flex justify-center mb-5">
            <div className="relative w-20 h-20 rounded-full border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] flex items-center justify-center">
              <Zap size={30} className="phosphor-glow" />
              <div className="absolute inset-2 rounded-full border border-dashed border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] animate-spin" style={{ animationDuration: "3.4s" }} />
            </div>
          </div>
          <div className="text-center text-xl md:text-2xl tracking-[0.12em] chromatic phosphor-glow mb-2">{headline}</div>
          <div className="text-center text-[9px] tracking-[0.28em] opacity-60 mb-7">{sub}</div>
          <div className="h-3 border border-[color-mix(in_srgb,var(--phosphor)_45%,transparent)] bg-black overflow-hidden">
            <div className="h-full bg-[var(--phosphor)] transition-[width] duration-100" style={{ width: phase === "dialup" ? "18%" : phase === "handshake" ? "46%" : `${Math.max(58, scanLine)}%` }} />
          </div>
          <div className="mt-3 text-[8px] tracking-[0.2em] opacity-40 flex justify-between"><span>NO CARRIER? NEVER.</span><span>CRT-NET/96</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-30 bg-black text-[var(--phosphor)] overflow-hidden" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="updates-overlay">
      <div className="absolute top-0 left-0 right-0 h-3 flex">
        {["#C7C700", "#00C7C7", "#00C700", "#C700C7", "#C70000", "#0000C7", "#FFFFFF"].map((c) => <div key={c} style={{ background: c, flex: 1 }} />)}
      </div>
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{ background: "repeating-linear-gradient(0deg, color-mix(in srgb, var(--phosphor) 5%, transparent) 0 1px, transparent 1px 4px)" }} />

      <div className="relative h-full flex flex-col px-5 md:px-7 pt-8 pb-5" data-testid="updates-whats-new">
        <header className="flex items-end justify-between gap-4 border-b border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] pb-3 mb-4">
          <div>
            <div className="text-[9px] tracking-[0.34em] opacity-55 mb-1">SCARTLEAD.TV NETWORK SERVICES</div>
            <div className="text-xl md:text-3xl tracking-[0.08em] chromatic phosphor-glow flex items-center gap-3"><Sparkles size={22} />WHAT'S NEW</div>
          </div>
          <div className="hidden sm:block text-right text-[8px] tracking-[0.18em] opacity-45"><div>UPDATE CHANNEL</div><div>STATUS · ONLINE</div></div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-crt pr-2">
          <section className="mb-6">
            <div className="flex items-center justify-between mb-2"><div className="text-[9px] tracking-[0.26em] opacity-65">WHAT'S NEW ON SCARTLEAD.TV</div><div className="text-[8px] opacity-40">LATEST FEATURES</div></div>
            {features.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="updates-release-feed">
                {features.map((item, i) => <FeatureCard key={item.id} item={item} index={i} />)}
              </div>
            ) : (
              <div className="border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] bg-black/65 p-4 text-[10px] tracking-[0.16em] opacity-55" data-testid="updates-release-feed-empty">
                RELEASE FEED TEMPORARILY UNAVAILABLE
              </div>
            )}
          </section>

          {notes && notes.trim() ? (
            <section className="mb-6">
              <div className="text-[9px] tracking-[0.26em] opacity-65 mb-2">STATION BULLETIN</div>
              <div className="text-[11px] md:text-xs leading-relaxed opacity-75 whitespace-pre-wrap border border-[color-mix(in_srgb,var(--phosphor)_22%,transparent)] bg-black/65 p-4" data-testid="updates-notes">{notes}</div>
            </section>
          ) : null}

          {recents.length > 0 && (
            <section className="mb-4">
              <div className="flex items-center justify-between mb-2"><div className="text-[9px] tracking-[0.26em] opacity-65">RECENTLY ADDED</div><div className="text-[8px] opacity-40">TUNE IN DIRECTLY</div></div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="updates-recent-videos">
                {recents.map((v, i) => <RecentCard key={v.id} video={v} index={i} onTuneToVideo={onTuneToVideo} />)}
              </div>
            </section>
          )}
        </div>

        <div className="pt-4 flex items-center justify-between gap-4 border-t border-[color-mix(in_srgb,var(--phosphor)_18%,transparent)]">
          <div className="text-[8px] tracking-[0.18em] opacity-35 hidden sm:block">CRT-1986 · NETWORK UPDATE SERVICE</div>
          <button onClick={onClose} autoFocus data-testid="updates-continue" className="ml-auto px-8 py-2 border-2 border-[var(--phosphor)] text-[var(--phosphor)] tracking-[0.25em] text-xs hover:bg-[var(--phosphor)] hover:text-black transition-colors phosphor-glow font-bold">▶ RETURN TO TV</button>
        </div>
      </div>
    </div>
  );
}
